import { debounce } from 'lodash'
import { useMemo } from 'react'
import { AsyncMultiSelectFilter } from './AsyncMultiSelectFilter'
import BetweenFilter from './BetweenFilter'
import MultiSelectFilter from './MultiSelectFilter'
import NumberFilter from './NumberFilter'
import { default as Combo } from 'app/components/ui/combo/combo'
import { default as ComboAsync } from 'app/components/ui/combo/combo-async'
import { default as ComboMultiSelectAsync } from 'app/components/ui/combo/combo-multi-select-async'
import { FilterObject, FilterOperator, FilterValueType, FilterableColumn } from 'app/filter.utils'
import { cn } from 'app/lib/utils'
import { MAX_DAY_WINDOW_DAYS, readDayWindow } from 'lib/dayWindowChoices'
import { OptionType } from 'types/OptionType'

export default function FilterConfig({
  filter,
  column,
  operator,
  contextualValue,
  openFilterOptions = false,
  onValueChange,
  onArrayFilterChange,
}: {
  filter: FilterObject
  column: FilterableColumn
  operator: FilterOperator
  contextualValue?: string | string[]
  openFilterOptions?: boolean
  onValueChange: (val: string | string[]) => void
  onArrayFilterChange?: React.Dispatch<React.SetStateAction<any>>
}) {
  const value = useMemo(() => filter.value, [filter])
  const dayWindow = column.dayWindows && readDayWindow(column.dayWindows, value)
  const choiceOptions = [
    ...(filter.options ?? []),
    ...(column.dayWindows ?? []).map((choice) => ({ id: choice.kind, name: choice.label, value: choice.kind })),
  ]
  const chosenOption = dayWindow
    ? { id: dayWindow.choice.kind, name: dayWindow.choice.before, value: dayWindow.choice.kind }
    : choiceOptions.find((option) => option.value === filter.value)
  return (
    <div>
      {/* Works for both operator === 'contains' & operator === 'exactly' */}
      {filter.type === FilterValueType.string && (
        <input
          readOnly={column.readOnly}
          type="text"
          className="rounded-md border border-strong bg-surface py-2 text-sm focus:text-strong"
          defaultValue={value as string}
          disabled={column.readOnly}
          onChange={debounce((e) => onValueChange(e.target.value), 300)}
        />
      )}
      {[FilterValueType.id, FilterValueType.stringArray].includes(filter.type) && (
        <AsyncMultiSelectFilter
          column={column}
          contextualValue={contextualValue}
          filter={filter}
          openFilterOptions={openFilterOptions}
          onValueChange={onValueChange}
        />
      )}
      {filter.type === FilterValueType.multiple && (
        <MultiSelectFilter
          filter={filter}
          value={value as Array<string>}
          onValueChange={onValueChange}
          onArrayFilterChange={onArrayFilterChange}
        />
      )}
      {(filter.type === FilterValueType.number || filter.type === FilterValueType.age) && (
        <>
          {operator === `between` ? (
            <BetweenFilter filter={filter} value={value as string[]} onValueChange={onValueChange} />
          ) : (
            <NumberFilter value={value} column={column} onValueChange={onValueChange} />
          )}
        </>
      )}
      {(filter.type === FilterValueType.boolean ||
        filter.type === FilterValueType.pmsIntegration ||
        filter.type === FilterValueType.attributeExists ||
        filter.type === FilterValueType.date) && (
        <div className={cn(dayWindow && `flex items-center gap-2`)}>
          <Combo
            className={cn(dayWindow && `w-32 shrink-0`)}
            inputClassName="!h-8 cursor-pointer rounded-lg border border-default !px-2 text-body-sm text-strong"
            optionsContainerClassName={cn(column.dayWindows && `normal-case`)}
            defaultValue={chosenOption}
            value={chosenOption}
            options={choiceOptions}
            onChange={(option) => {
              const choice = column.dayWindows?.find((candidate) => candidate.kind === option.value)
              onValueChange(choice ? `${choice.kind}:${choice.defaultDays}` : option.value!)
            }}
            disabled={column.readOnly}
            showButtonTooltip
          />
          {dayWindow && (
            <>
              <div className="w-16 shrink-0" data-testid="filter-day-count">
                <NumberFilter
                  key={dayWindow.choice.kind}
                  column={column}
                  value={dayWindow.days}
                  onValueChange={(days) => {
                    const count = Number(days)
                    if (Number.isInteger(count) && count >= 1 && count <= MAX_DAY_WINDOW_DAYS)
                      onValueChange(`${dayWindow.choice.kind}:${count}`)
                  }}
                />
              </div>
              <span className="shrink-0 text-body-sm text-default">{dayWindow.choice.after}</span>
            </>
          )}
        </div>
      )}
      {filter.type === FilterValueType.asyncSelect && (
        <ComboAsync
          placeholder="Select"
          name={filter.displayName}
          inputClassName="!h-8 cursor-pointer rounded-lg border border-default !px-2 !text-body-sm !pt-3"
          optionsMapper={(res) => [
            { name: `Any`, id: `*`, value: `*` },
            ...(res.hits?.map((item) => ({ id: item, value: item, name: item })) ?? []),
          ]}
          queryURL={column.optionsSource!}
          fetcher={column.fetcher}
          value={
            {
              id: filter.value,
              name: filter?.value === `*` ? `Any` : filter.value,
              value: filter.value === `*` ? `Any` : filter.value,
            } as any
          }
          onChange={(option) => {
            onValueChange(option.value!)
          }}
          disabled={column.readOnly}
          enableDeselectAllOption
          deselectAllOptions={[{ name: `Any`, id: `*`, value: `*` }] as any}
        />
      )}
      {filter.type === FilterValueType.asyncMultiSelect && (
        <ComboMultiSelectAsync
          placeholder="Select"
          name={filter.displayName}
          inputClassName="!h-8 cursor-pointer rounded-lg border border-default !px-2 !text-body-sm !pt-3"
          // `res` is the `{ hits }` response when populating the dropdown list, or a
          // raw string[] when seeding the selected options synchronously from `values`.
          optionsMapper={(res) =>
            (Array.isArray(res) ? res : (res.hits ?? [])).map((item) => ({ id: item, value: item, name: item }))
          }
          queryURL={column.optionsSource}
          fetcher={column.fetcher}
          // The values are already the display strings, so seed the selected options
          // from them (queryValues=false) instead of re-fetching — avoids the "Select"
          // placeholder flashing before the async round-trip resolves. Pass [] (not
          // ['*']) for the empty/wildcard case so it renders as "Any".
          values={value == null || value === `*` ? [] : Array.isArray(value) ? value : [value]}
          queryValues={false}
          onChangeHandler={(options) => {
            onValueChange(options.map((option) => option.value!))
          }}
          disabled={column.readOnly}
          enableDeselectAllOption
          deselectAllOption={{ name: `Any`, id: `*`, value: `*` } as OptionType}
        />
      )}
    </div>
  )
}
